import { defineStore } from 'pinia';
import { TenantIntegrationService } from '@/services';
import { extractErrorMessage } from '@/utils';
import { useAuthStore } from '@/store';

export const useTenantIntegrationStore = defineStore('TenantIntegrationStore', {
  state: () => ({
    roles: [],
    users: [],
    // eslint-disable-next-line
    // @ts-ignore
    integrations: [] as TenantIntegrationInterface[],
    initialLoading: false,
    loading: false,
    error: null as string | null,
    openModal: false,
    modalAction: 'create' as string,
  }),

  actions: {
    async getRoles(tenantId: number) {
      this.loading = true;

      try {
        const response = await TenantIntegrationService.getRoles(tenantId);
        this.roles = response.data.data;
      } catch (err) {
        this.error = 'Failed to fetch available roles';
      } finally {
        this.loading = false;
      }
    },

    async getUsers(tenantId: number) {
      this.loading = true;

      try {
        const response = await TenantIntegrationService.getUsers(tenantId);
        this.users = response.data.data;
      } catch (err) {
        this.error = 'Failed to fetch available users';
      } finally {
        this.loading = false;
      }
    },

    async saveTenantIntegration(tenantId: number, payload: FormData) {
      this.loading = true;
      this.error = null;

      try {
        const response = await TenantIntegrationService.saveIntegration(tenantId, payload);
        this.integrations.push(response.data);
        this.defineSuccessMessage({
          title: 'Success',
          message: 'Integration information has been saved',
        });
      } catch (error) {
        this.error = extractErrorMessage(error);
        this.defineErrorMessage({
          title: 'Error',
          message: 'There was an error saving the Integration information',
        });
      } finally {
        this.loading = false;
      }
    },

    async updateTenantIntegration(tenantId: number, integrationId: number, payload: FormData) {
      this.loading = true;
      this.error = null;

      try {
        const response = await TenantIntegrationService.updateIntegration(tenantId, integrationId, payload);
        const integrationKey = this.integrations.findIndex((item: any) => item.id === response.data.id);
        this.integrations[integrationKey] = response.data;
        this.defineSuccessMessage({
          title: 'Success',
          message: 'Integration information has been updated',
        });
      } catch (error) {
        this.error = extractErrorMessage(error);
        this.defineErrorMessage({
          title: 'Error',
          message: 'There was an error updating the Integration information',
        });
      } finally {
        this.loading = false;
      }
    },

    async saveTenantIntegrationOrganization(tenantId: number, integrationId: number, payload: FormData) {
      this.loading = true;
      this.error = null;

      try {
        const response = await TenantIntegrationService.saveTenantIntegrationOrganization(
          tenantId,
          integrationId,
          payload,
        );
        await this.defineTenantIntegrationOrganizationData(response, integrationId);
      } catch (error) {
        this.defineErrorMessage(error);
      } finally {
        this.loading = false;
      }
    },

    async updateTenantIntegrationOrganization(tenantId: number, integrationId: number, id: number, payload: FormData) {
      this.loading = true;
      this.error = null;

      try {
        const response = await TenantIntegrationService.updateTenantIntegrationOrganization(
          tenantId,
          integrationId,
          id,
          payload,
        );
        await this.defineTenantIntegrationOrganizationData(response, integrationId);
      } catch (error) {
        this.defineErrorMessage(error);
      } finally {
        this.loading = false;
      }
    },

    async deleteTenantIntegrationOrganization(tenantId: number, integrationId: number, id: number) {
      this.loading = true;
      this.error = null;

      try {
        const response = await TenantIntegrationService.deleteTenantIntegrationOrganization(
          tenantId,
          integrationId,
          id,
        );
        await this.defineTenantIntegrationOrganizationData(response, integrationId);
      } catch (error) {
        this.defineErrorMessage(error);
      } finally {
        this.loading = false;
      }
    },

    // eslint-disable-next-line
    // @ts-ignore
    async defineTenantIntegrationOrganizationData(response, integrationId: number) {
      const message = response.data.success;
      const integrationIndex = this.integrations.findIndex((item: any) => item.id === integrationId);
      // eslint-disable-next-line
      // @ts-ignore
      const orgs = response.data.data.sort((a, b) => a.organization_name.localeCompare(b.organization_name));
      this.integrations[integrationIndex].integration_organizations = orgs;
      this.defineSuccessMessage(message);
    },

    findIntegration(provider: string) {
      return this.integrations.find((integration: any) => integration.integration_provider_type.slug === provider);
    },

    async getTenantIntegrations(tenantId: number) {
      this.initialLoading = true;
      this.error = null;

      try {
        const { data } = await TenantIntegrationService.getIntegration(tenantId);
        this.integrations = data.data;
      } catch (error) {
        this.error = extractErrorMessage(error);
      } finally {
        this.initialLoading = false;
      }
    },

    async getIntegrations() {
      const authStore = useAuthStore();
      if (authStore.token?.tenant?.id) {
        return this.getTenantIntegrations(authStore.token.tenant.id);
      }
    },

    defineSuccessMessage(message: { message: string; title: string }) {
      this.doToast(message.message, message.title);
    },

    // eslint-disable-next-line
    // @ts-ignore
    defineErrorMessage(error) {
      this.doToast(error.data.error.message, error.data.error.title, true);
    },
  },
});
